import { useMemo } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import TypeService from "../../../../service/TypeService";
import SubcategoriesService from "../../../../service/SubcategoriesService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import useCatalogForm from "../../hooks/useCatalogForm";
import { getToken } from "../../utils/adminUtils";
import {
  clearFilled,
  emptyTranslations,
  findNewestIdBy,
  linkToSubcategories,
  localizedDescription,
  missingTranslations,
  otherTranslations,
  pickTranslations,
  subcategoryOptions,
  translationsSearchText,
} from "../../utils/catalogUtils";
import CatalogTable from "../../components/list/CatalogTable";
import FormDialog from "../../components/form/FormDialog";
import TranslationFields from "../../components/form/TranslationFields";
import SubcategoryPicker from "../../components/form/SubcategoryPicker";

const LIST_PATH = "/dashboard-listtypes";

const loadTypesWithSubcategories = async () => {
  const [typesRes, subRes] = await Promise.all([
    new TypeService().getType(getToken()),
    new SubcategoriesService().getSubcategories(getToken()),
  ]);
  return [
    {
      types: expectCode(typesRes, 200001).result || [],
      subcategories: expectCode(subRes, 200001).result || [],
    },
  ];
};

const AdminTypes = ({ startCreating = false }) => {
  const { showErrorToast } = useToastContext();
  const { items, loading, loadError, reload } = useAdminList(loadTypesWithSubcategories);
  const { run, runningId } = useAdminAction();
  const data = items[0];

  const options = useMemo(() => subcategoryOptions(data?.subcategories), [data]);

  // Se a API trouxer os tipos vinculados em cada subcategoria (`types`), mostra os vínculos atuais
  const linkedByType = useMemo(() => {
    const map = new Map();
    (data?.subcategories || []).forEach((sub) =>
      (sub.types || []).forEach((type) => {
        if (!map.has(type.id)) map.set(type.id, []);
        map.get(type.id).push({ id: sub.id, label: localizedDescription(sub) });
      }),
    );
    return map;
  }, [data]);

  const buildInitial = (item) => ({
    values: item ? pickTranslations(item) : emptyTranslations(),
    subcategoryIds: [],
    errors: {},
  });

  const { form, openCreate, openEdit, close, update } = useCatalogForm({
    startCreating,
    listPath: LIST_PATH,
    buildInitial,
  });

  const rows = useMemo(
    () =>
      (data?.types || []).map((type) => ({
        ...type,
        name: localizedDescription(type),
        searchText: translationsSearchText(type),
      })),
    [data],
  );

  const save = async () => {
    const errors = {};
    missingTranslations(form.values).forEach(({ field }) => {
      errors[field] = localeText("adminFormRequired");
    });
    // Como no admin clássico, um tipo novo precisa de ao menos uma subcategoria
    if (form.mode === "create" && form.subcategoryIds.length === 0) {
      errors.subcategories = localeText("chooseSubcategory");
    }
    if (Object.keys(errors).length) {
      update({ errors });
      return;
    }

    const { values, item, subcategoryIds, mode } = form;
    const descriptionEnglish = values.descriptionEnglish.trim();
    let failedLinks = 0;

    const ok = await run(
      "form",
      async () => {
        const service = new TypeService();
        const descriptions = [
          descriptionEnglish,
          values.descriptionItalian.trim(),
          values.descriptionPortuguese.trim(),
          values.descriptionSpanish.trim(),
        ];
        const res =
          mode === "create"
            ? await service.postRegisterType(getToken(), ...descriptions)
            : await service.postEditType(getToken(), ...descriptions, item.id);
        expectCode(res, 200000);

        if (subcategoryIds.length === 0) return;
        let typeId = item?.id;
        if (mode === "create") {
          const typesRes = await service.getType(getToken());
          typeId = findNewestIdBy(
            typesRes?.result,
            (type) => type.descriptionEnglish === descriptionEnglish,
          );
        }
        failedLinks = typeId == null
          ? subcategoryIds.length
          : await linkToSubcategories("type", typeId, subcategoryIds);
      },
      "adminCatalogSaved",
    );

    if (failedLinks > 0) showErrorToast({ detail: "adminCatalogLinkFailed" });
    if (ok) {
      close();
      reload();
    }
  };

  return (
    <div className="admin-list">
      <FormDialog
        visible={!!form}
        title={localeText(form?.mode === "edit" ? "adminCatalogEditType" : "addNewType")}
        subtitle={form?.mode === "edit" ? localizedDescription(form.item) : localeText("typeFields")}
        saving={runningId === "form"}
        onSave={save}
        onCancel={close}
      >
        {form && (
          <>
            <TranslationFields
              idPrefix="type"
              values={form.values}
              errors={form.errors}
              onChange={(values) =>
                update({ values, errors: { ...form.errors, ...clearFilled(values) } })
              }
              autoFocus
            />
            <SubcategoryPicker
              options={options}
              lockedOptions={form.mode === "edit" ? linkedByType.get(form.item.id) || [] : []}
              value={form.subcategoryIds}
              onChange={(subcategoryIds) =>
                update({ subcategoryIds, errors: { ...form.errors, subcategories: null } })
              }
              required={form.mode === "create"}
              error={form.errors.subcategories}
              hint={form.mode === "edit" ? localeText("adminCatalogLinkHint") : null}
            />
          </>
        )}
      </FormDialog>

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminCatalogSearchName")}
        createLabel={localeText("newType")}
        onCreate={openCreate}
        sortField="name"
        minWidth="36rem"
      >
        <Column
          header={localeText("types")}
          field="name"
          body={(type) => (
            <div className="admin-stack">
              <span className="admin-strong">{type.name || "—"}</span>
              <span className="admin-catalog__translations">{otherTranslations(type)}</span>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(type) => (
            <button type="button" className="admin-btn" onClick={() => openEdit(type)}>
              <i className="pi pi-pencil" aria-hidden="true" />
              {localeText("edit")}
            </button>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminTypes;
